import React, { useCallback, useMemo, useState } from 'react';

import CollapsedContent from 'components/uiLibrary/CollapsedContent';
import TabSwitch from 'components/uiLibrary/TabSwitch';
import Typography from 'components/uiLibrary/Typography';

import CommonTrans from 'components/Globals/CommonTrans';
import StickyContentMarker from 'components/Globals/StickyHeaders';

import { TP } from 'constants/index';
import { SUBSCRIPTION_GROUP_DETAILS, SUBSCRIPTION_GROUP_IDENTIFIERS } from 'constants/subscriptions';

import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';

import { useTranslation } from 'src/i18n';

import PaywallFAQ from '../PaywallFAQ';
import PlanGroupDetails, { FeatureTable, GroupFeature } from '../PlanGroupListing/PlanGroupDetails';
import PlanListing from '../PlanListing';
import PriceRange from '../PriceRange';

import classes from './PlanGroupView.module.scss';

const TABS = {
  FEATURES: 'features',
  TOOLS: 'tools',
  FAQ: 'faq',
};

const MobileGroupView = ({ identifier }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { priceOptions, features, tools, featureTable } = SUBSCRIPTION_GROUP_DETAILS[identifier];

  const hasFeatures = !!features;
  const hasTools = !!tools || !!featureTable;
  const hasFAQ = [
    SUBSCRIPTION_GROUP_IDENTIFIERS.ARTIST_PROFESSIONAL,
    SUBSCRIPTION_GROUP_IDENTIFIERS.CASTING_TOOL,
  ].includes(identifier);

  const hasMoreThanOneTab = [hasFeatures, hasTools, hasFAQ].filter(Boolean).length > 1;

  const [activeTab, setActiveTab] = useState(() => {
    if (hasFeatures) {
      return TABS.FEATURES;
    }

    if (hasTools) {
      return TABS.TOOLS;
    }

    return TABS.FAQ;
  }, []);

  const options = useMemo(
    () => [
      ...(hasFeatures ? [{ label: t(`${TP}.FN_WHAT_YOU_GET`), value: TABS.FEATURES }] : []),
      ...(hasTools ? [{ label: t(`${TP}.FN_FEATURES`), value: TABS.TOOLS }] : []),
      ...(hasFAQ ? [{ label: t(`${TP}.m_FAQ`), value: TABS.FAQ }] : []),
    ],
    [t, hasFeatures, hasTools, hasFAQ],
  );

  const maxHeightPx = useMemo(() => {
    if (identifier === SUBSCRIPTION_GROUP_IDENTIFIERS.CASTING_TOOL) {
      return 230;
    }

    return 280;
  }, [identifier]);

  const onTabChange = useCallback((e, option) => {
    setActiveTab(option?.value);
  }, []);

  return (
    <div className={classes.mobileGroupView}>
      {hasMoreThanOneTab && (
        <div className={classes.tabSwitch}>
          <TabSwitch options={options} value={activeTab} onChange={onTabChange} />
        </div>
      )}
      <div className={classes.tabContent}>
        {activeTab === TABS.FEATURES && !!features && (
          <CollapsedContent maxHeightPx={maxHeightPx} collapseThresholdPx={20}>
            <div className={classes.tools}>
              <GroupFeature i18nKey={features} />
            </div>
          </CollapsedContent>
        )}
        {activeTab === TABS.TOOLS && !!featureTable && (
          <CollapsedContent maxHeightPx={maxHeightPx} collapseThresholdPx={20}>
            <div className={classes.tools}>
              <FeatureTable priceOptions={priceOptions} data={featureTable} />
            </div>
          </CollapsedContent>
        )}
        {activeTab === TABS.TOOLS && !!tools && (
          <CollapsedContent maxHeightPx={maxHeightPx} collapseThresholdPx={20}>
            <div className={classes.tools}>
              <GroupFeature i18nKey={tools} />
            </div>
          </CollapsedContent>
        )}
        {activeTab === TABS.FAQ && (
          <CollapsedContent maxHeightPx={maxHeightPx} collapseThresholdPx={20}>
            <PaywallFAQ identifier={identifier} />
          </CollapsedContent>
        )}
      </div>
    </div>
  );
};

const PlanGroupView = ({ identifier, onSelectPlan, subscription, isRedirecting, processingProductIds }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { isDesktop } = useDeviceTypeLayouts();

  return (
    <div className={classes.root}>
      <div className={classes.details}>
        <StickyContentMarker
          stackIdentifier="primary"
          name="title"
          renderHeader={() => (
            <div className={classes.stickyTitle}>
              <Typography variant="p" weight="bold">
                {t(SUBSCRIPTION_GROUP_DETAILS[identifier]?.title)}
              </Typography>
              <PriceRange price={SUBSCRIPTION_GROUP_DETAILS[identifier].price} priceSize={16} />
            </div>
          )}
        />
        <PlanGroupDetails identifier={identifier} showBasicInfo={!isDesktop} skipPrice />
      </div>
      <div className={classes.plans}>
        <PlanListing
          groupIdentifier={identifier}
          onClick={onSelectPlan}
          subscription={subscription}
          isRedirecting={isRedirecting}
          processingProductIds={processingProductIds}
        />
        {SUBSCRIPTION_GROUP_DETAILS[identifier]?.planNote && (
          <div className={classes.planNote}>
            <Typography variant="p" size={13}>
              <CommonTrans i18nKey={SUBSCRIPTION_GROUP_DETAILS[identifier]?.planNote} ns="NS_APP_GLOBALS" />
            </Typography>
          </div>
        )}
        {!isDesktop && <MobileGroupView identifier={identifier} />}
        {isDesktop && <PaywallFAQ className={classes.faq} identifier={identifier} />}
      </div>
    </div>
  );
};

export default PlanGroupView;
